import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';
import { useHelp } from './HelpContext';
import { helpTopics } from '../../help';

const HelpMarkdownLink: React.FC<{ href?: string; children?: React.ReactNode }> = ({ href, children }) => {
  const { openHelp } = useHelp();
  if (href?.startsWith('help:')) {
    const slug = href.replace('help:', '');
    return (
      <button onClick={() => openHelp(slug)} className="text-primary hover:text-primary-dark underline">
        {children}
      </button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-dark underline">
      {children}
    </a>
  );
};

const HELP_MD_COMPONENTS = { a: HelpMarkdownLink };

export function HelpPanel() {
  const { isOpen, currentTopic, closeHelp, openHelp } = useHelp();
  const [showTopics, setShowTopics] = useState(false);
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // Handle mount/unmount with animation
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimateIn(true));
      });
    } else {
      setAnimateIn(false);
      const timer = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeHelp();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, closeHelp]);

  if (!visible) return null;

  const topic = helpTopics[currentTopic] || helpTopics['getting-started'];
  const isFallback = !helpTopics[currentTopic];

  // Group topics by category
  const categories: Record<string, { slug: string; title: string }[]> = {};
  for (const [slug, t] of Object.entries(helpTopics)) {
    if (!categories[t.category]) categories[t.category] = [];
    categories[t.category].push({ slug, title: t.title });
  }

  return (
    <div className="fixed inset-0 z-[60]">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/20 transition-opacity duration-300 ${
          animateIn ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={closeHelp}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`absolute top-0 right-0 h-full w-[420px] max-w-[90vw] bg-white shadow-xl flex flex-col transition-transform duration-300 ease-in-out ${
          animateIn ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50/80">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-4 h-4 text-primary" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900 truncate">{topic?.title || 'Help'}</h2>
          </div>
          <button
            onClick={closeHelp}
            className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Fallback notice */}
        {isFallback && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 text-xs text-amber-700">
            Showing general help. This page doesn't have a dedicated help article yet.
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="help-markdown">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={HELP_MD_COMPONENTS}
            >
              {topic?.content || ''}
            </ReactMarkdown>
          </div>
        </div>

        {/* Browse Topics Footer */}
        <div className="border-t border-gray-200">
          <button
            onClick={() => setShowTopics(!showTopics)}
            className="flex items-center justify-between w-full px-5 py-3 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <span>Browse Topics</span>
            {showTopics ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>

          {showTopics && (
            <div className="px-5 pb-3 max-h-48 overflow-y-auto">
              {Object.entries(categories).map(([category, topics]) => (
                <div key={category} className="mb-2">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{category}</div>
                  {topics.map(({ slug, title }) => (
                    <button
                      key={slug}
                      onClick={() => openHelp(slug)}
                      className={`block w-full text-left px-2 py-1 text-xs rounded transition-colors ${
                        currentTopic === slug
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {title}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Scoped markdown styles */}
      <style>{`
        .help-markdown h1 { font-size: 1.25rem; font-weight: 700; color: #0f172a; margin-bottom: 0.75rem; }
        .help-markdown h2 { font-size: 1rem; font-weight: 600; color: #0f172a; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .help-markdown h3 { font-size: 0.875rem; font-weight: 600; color: #334155; margin-top: 1.25rem; margin-bottom: 0.375rem; }
        .help-markdown p { font-size: 0.8125rem; line-height: 1.6; color: #475569; margin-bottom: 0.625rem; }
        .help-markdown ul, .help-markdown ol { font-size: 0.8125rem; color: #475569; padding-left: 1.25rem; margin-bottom: 0.625rem; }
        .help-markdown li { margin-bottom: 0.25rem; line-height: 1.5; }
        .help-markdown strong { color: #1e293b; font-weight: 600; }
        .help-markdown hr { border: none; border-top: 1px solid #e2e8f0; margin: 1.25rem 0; }
        .help-markdown table { width: 100%; border-collapse: collapse; font-size: 0.75rem; margin-bottom: 0.75rem; }
        .help-markdown th { text-align: left; padding: 0.375rem 0.5rem; border-bottom: 2px solid #e2e8f0; font-weight: 600; color: #334155; }
        .help-markdown td { padding: 0.375rem 0.5rem; border-bottom: 1px solid #f1f5f9; color: #475569; }
        .help-markdown code { background: #f1f5f9; padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.75rem; }
        .help-markdown blockquote { border-left: 3px solid #00c7c4; padding-left: 0.75rem; color: #64748b; font-style: italic; margin: 0.75rem 0; }
      `}</style>
    </div>
  );
}
